(ns ui.subscription.buy
  (:require [parts.replicant.command :as command]
            [parts.replicant.query :as query]
            [storrito.calendly.links :as calendly]
            [tick.core :as t]
            [ui.elements]
            [storrito.ui.core :as ui]
            [lambdaisland.uri :as uri]
            [ui.subscription.core :as core]
            [ui.subscription.part :as part]
            [ui.campaign-banner]
            [ui.user-session :as user-session]))

(def subscription-query
  {:query/kind :query/subscription})

(defn format-price
  [price]
  (str "$"
       (.toFixed (or price 0)
                 2)))

(defn get-billing-interval
  [state]
  (or (:billing-interval state)
      :month))

(defn set-current-instagram-seats
  [w]
  (assoc-in w
            [:services :instagram-connection :default-units]
            (Math/max 1
                      (count (:instagram-accounts w)))))

(defn add-selected-quantity
  [w]
  (assoc w
    :selected-quantity (long (js/Math.ceil (:receipt-total w)))))

(defn add-selected-interval
  [w]
  (assoc w
    :selected-interval (name (get-billing-interval w))))

(defn add-current-quantity
  [w]
  (assoc w
    :current-quantity (get-in w
                              [:subscription-item
                               :quantity])))

(defn add-current-interval
  [w]
  (assoc w
    :current-interval (get-in w
                              [:subscription-item
                               :price
                               :billing_cycle
                               :interval])))

(defn discount-badge
  "Shows a discount badge with the discount percentage or name."
  [discount]
  (when discount
    [:span.inline-flex.items-center.rounded-md.bg-green-50.px-2.py-1.text-xs.font-medium.text-green-700.ring-1.ring-inset.ring-green-600
     (str (int (* 100 discount)) "% OFF")]))

(defn format-discounted-price
  "Shows original price (strikethrough) and discounted price when discount applies."
  [original-price discounted-price discount]
  (if (and discount (not= original-price discounted-price))
    [:div.flex.items-baseline.justify-end
     [:span.text-sm.text-gray-500.line-through
      (format-price original-price)]
     [:span.text-green-600.font-semibold.ml-2
      (format-price discounted-price)]]
    [:span (format-price original-price)]))

(defn savings-panel
  [w]
  ;; Show savings message if there are any discounts
  (when (and (:total-savings w)
             (pos? (:total-savings w)))
    (let [campaign (:campaign (query/get-result w
                                                subscription-query))]
      [:div.mt-4.px-4.py-3.bg-green-50.rounded-lg.border.border-green-200
       [:p.text-sm.text-green-700.font-semibold.text-center
        (when campaign
          [:div.mb-4
           (ui.campaign-banner/discount-description campaign nil)])

        "You're saving " (format-price (:total-savings w)) "/" (name (get-billing-interval w))
        (when-not campaign " with active discounts!")]])))

(defn how-to
  [_state]
  [:section {:class ui.elements/panel}
   [:h2 {:class ui.elements/h3}
    "How our pricing works"]
   [:p
    {:class (concat ui.elements/p-sm
                    ["mt-2" "mb-6"])}
    "You only pay for what you use:"]
   [:ol {:class "ml-4 list-decimal list-outside"}
    [:li {:class "mb-2"}
     [:strong {:class "font-semibold"} "Add Funds to Your Wallet"]
     [:br]
     [:span.text-gray-500 "At the checkout choose an amount you’re comfortable with."]]
    [:li {:class "mb-2"}
     [:strong {:class "font-semibold"} "Use Our App, Pay a Tiny Fee per Service"]
     [:br]
     [:span.text-gray-500 "A small amount is deducted from your wallet for each paid service."]]
    [:li {:class "mb-2"}
     [:strong {:class "font-semibold"} "Automatic Top-Up"]
     [:br]
     [:span.text-gray-500 "Every month (or year), we automatically refill your wallet by charging your card for the amount you’ve set."]]
    [:li {:class "mb-2"}
     [:strong {:class "font-semibold"} "Stop Anytime"]
     [:br]
     [:span.text-gray-500 "If you no longer need Storrito, cancel the refill. Credits are non-refundable."]]]])

(defn error-panel
  [w]
  (when-let [error (get (command/get-latest-result w
                                                   (:subscription-update-command w))
                        "error")]
    (let [error-message (case (get error "code")
                          "subscription_payment_declined"
                          "The payment was declined. Please contact your bank/payment provider and try again."

                          ("subscription_is_canceled_action_invalid" "subscription_update_when_canceled")
                          "Payment failed, the subscription is already canceled."

                          "subscription_update_when_past_due"
                          "Payment failed. Your subscription cannot be updated because it is past due."

                          "subscription_next_billed_at_too_soon"
                          "Subscription cannot be updated because the next payment is too soon. Please try again later."

                          (str "Payment failed! "
                               (or (get error "detail")
                                   "Unknown error. Please contact support."))
                          )]
      [:div.text-red-500.my-4.italic error-message])))

(defn pricing-table
  [state]
  (let [discounts (:discounts (query/get-result state subscription-query))]
    [:section
     {:class (concat ui.elements/panel
                     )}
     [:h2 {:class ui.elements/h3}
      "Our Rates at a Glance"]
     [:p
      {:class (concat ui.elements/p-sm
                      ["mt-2" "mb-6"])}
      "Please find below what we charge per service. "
      [:span.font-bold
       "All prices are quoted in US dollars excluding taxes (excl. VAT)."]]
     [:table
      {:class ["min-w-full" "divide-y" "divide-gray-300"]}

      [:tbody
       {:class "divide-y divide-gray-200"}
       (map
         (fn [{:keys [id name description price billing-unit]}]
           (let [discount (discounts id)
                 discounted-price (if discount
                                    (* price (- 1.0 discount))
                                    price)]
             [:tr
              [:td
               {:class
                "whitespace-nowrap py-2 pl-4 pr-3 text-sm sm:pl-0"}
               [:div.flex.items-center.gap-2
                [:span name]
                (discount-badge discount)]

               [:span {:class "text-gray-500"}
                description]]
              [:td
               {:class "whitespace-nowrap px-2 py-2 text-sm"}
               (if (and discount (not= price discounted-price))
                 [:div
                  [:span.text-xs.text-gray-400.line-through
                   (format-price price)]
                  [:br]
                  [:span.text-green-600.font-semibold
                   (format-price discounted-price)]]
                 (format-price price))
               [:div {:class "text-gray-500"}
                (case billing-unit
                  :day
                  "per day"
                  :post
                  "per post"
                  :image
                  "per image")]]
              [:td
               {:class "whitespace-nowrap px-2 py-2 text-sm"}
               (when (= billing-unit
                        :day)
                 (let [days 31
                       original-monthly (* price days)
                       discounted-monthly (* discounted-price days)]
                   (if (and discount (not= original-monthly discounted-monthly))
                     (list [:span.text-xs.text-gray-400.line-through
                            (format-price original-monthly)]
                           [:br]
                           [:span.text-green-600.font-semibold
                            (format-price discounted-monthly)]
                           [:br]
                           [:span {:class "text-gray-500"}
                            "per " days " days"])
                     (list
                       (format-price original-monthly)
                       [:br]
                       [:span {:class "text-gray-500"}
                        "per "
                        days
                        " days"]))))]]))
         (:services (query/get-result state
                                      subscription-query)))]]
     [:p.text-xs.text-gray-500.mt-4
      "All prices excl. VAT"]]))


(defn call-to-action
  [state]
  (let [data (query/get-result state
                               subscription-query)
        uri (-> data
                (:checkout-url)
                (uri/uri))]
    [:section {:class ["space-y-6"]}
     [:h2 {:class ui.elements/h2}
      "Ready to Buy?"]
     [:p {:class ["text-gray-600"]}
      "Pick monthly for smaller monthly charges, or yearly if you prefer fewer
       receipts to forward to your book keeper or tax advisor."]

     ;; Cards for monthly vs yearly
     [:div {:class ["grid" "grid-cols-1" "md:grid-cols-2" "gap-6"]}
      ;; Monthly Card
      [:div {:class (concat ui.elements/panel
                            ["flex" "flex-col" "items-center" "justify-between"])}
       [:div {:class ["space-y-2"]}
        [:h4 {:class ui.elements/h4} "Monthly Plan"]
        [:p {:class ["text-gray-500"]} "Smaller monthly payments. No annual commitment."]]
       [:button {:class ["mt-4"
                         "inline-block"
                         "px-5"
                         "py-3"
                         "bg-emerald-500"
                         "hover:bg-emerald-600"
                         "text-white"
                         "font-medium"
                         "rounded-md"
                         "transition"
                         "duration-150"]
                 :disabled (command/issued? state
                                             {:command/kind :command/claim-campaign
                                              :command/data {}})
                 :on {:click [[:js/alert
                               (str
                                 "The following price is a suggestion based on your "
                                 "connected Instagram accounts, the storage fee as well as "
                                 "a buffer to try out other Storrito features.")]
                              [:data/command
                               {:command/kind :command/claim-campaign
                                :command/data {}}
                               {:on-success [[:js/location (-> uri
                                                               (uri/assoc-query :interval
                                                                                "monthly"
                                                                                :quantity
                                                                                (:monthly-credit-suggestion data))
                                                               (uri/uri-str))]]}]]}}
        "Buy Monthly"]]

      ;; Yearly Card
      [:div {:class (concat ui.elements/panel
                            ["flex" "flex-col" "items-center" "justify-between"])}
       [:div {:class ["space-y-2"]}
        [:h4 {:class ui.elements/h4} "Yearly Plan"]
        [:p {:class ["text-gray-500"]} "One payment, fewer receipts. Same total cost."]]
       [:button {:class ["mt-4"
                         "inline-block"
                         "px-5"
                         "py-3"
                         "bg-indigo-500"
                         "hover:bg-indigo-600"
                         "text-white"
                         "font-medium"
                         "rounded-md"
                         "transition"
                         "duration-150"]
                 :disabled (command/issued? state
                                             {:command/kind :command/claim-campaign
                                              :command/data {}})
                 :on {:click [[:js/alert
                               (str
                                 "The following price is a suggestion based on your "
                                 "connected Instagram accounts, the storage fee as well as "
                                 "a buffer to try out other Storrito features.")]
                              [:data/command
                               {:command/kind :command/claim-campaign
                                :command/data {}}
                               {:on-success [[:js/location (-> uri
                                                               (uri/assoc-query :interval
                                                                                "yearly"
                                                                                :quantity
                                                                                (* (:monthly-credit-suggestion data)
                                                                                   12))
                                                               (uri/uri-str))]]}]]}}
        "Buy Yearly"]]]]))

(defn we-love
  []
  [:footer
   {:class (concat ui.elements/panel
                   ["text-center" "space-y-2"])}
   [:h3 {:class ui.elements/h4}
    "We love creating software that makes your day better "
    [:span {:class ["text-yellow-500"]} "\uD83E\uDD17"]]
   [:p {:class ["text-gray-600"]}
    "Every day we get up and use our energy to make Storrito simpler,
    better and more fun to use for you.\n\nYour purchase would really
    mean a lot to us and would support our small four-person team."]])

(defn intro
  []
  [:div.mt-12.mb-6
   [:p "Storrito runs on prepaid credits. Top up your wallet to use services, which are billed daily or per use."]

   [:p.mt-4 "Use the calculator below to estimate how much to add to your wallet. "
    [:span.font-bold
     "Unused credits roll over."]]])

(defn service-cell
  [{:keys [service] :as state}]
  (let [{:keys [units-min units-max]} service
        path [:receipt
              :quantity
              (:id service)]]
    [:td
     [:input.h-6.w-12.mr-3
      {:class ["bg-gray-50" "border" "border-gray-300" "text-black" "text-sm" "p-0" "pl-2"
               "focus:ring-blue-500" "focus:border-blue-500" "disabled:cursor-not-allowed"]
       :disabled (= units-min
                    units-max)
       :type "number"
       :min units-min
       :max units-max
       :on {:input [[:store/assoc-in path :event/target.value-int]]}
       :value (or (get-in state
                          path)
                  (:default-units service))}]]))

(defn service-price-cell
  [{:keys [service] :as w}]
  (let [{:keys [billing-unit days price]} service
        discount (part/get-service-discount w (:id service))
        discounted-price (if discount
                           (* price (- 1 discount))
                           price)]
    [:td.text-gray-600.flex
     (when (= billing-unit
              :day)
       [:div.mr-1 (str days
                       " days x ")])
     (if (and discount (not= price discounted-price))
       [:div
        [:span.text-sm.text-gray-400.line-through
         (format-price price)]
        [:span.ml-2.text-green-600
         (format-price discounted-price)]]
       (format-price price))
     "/"
     (name (or billing-unit :month))]))

(defn service-total
  [w]
  (let [original-total (part/calc-service-total w)
        discounted-total (part/calc-service-total-with-discount w)
        discount (part/get-service-discount w (get-in w [:service :id]))]
    [:td {:class ["pl-2" "text-right"]}
     (format-discounted-price original-total discounted-total discount)]))

(def core-services
  [:instagram-connection
   :storage-flat-fee])

(def optional-services
  [:ai-image
   :tiktok-post
   :youtube-short
   :social-post
   :x-post
   :x-post-link])

(def chevron-down-icon
  [:svg
   {:xmlns "http://www.w3.org/2000/svg",
    :fill "none",
    :viewBox "0 0 24 24",
    :stroke-width "1.5",
    :stroke "currentColor",
    :class "size-4"}
   [:path
    {:stroke-linecap "round",
     :stroke-linejoin "round",
     :d "m19.5 8.25-7.5 7.5-7.5-7.5"}]])

(def chevron-up-icon
  [:svg
   {:xmlns "http://www.w3.org/2000/svg",
    :fill "none",
    :viewBox "0 0 24 24",
    :stroke-width "1.5",
    :stroke "currentColor",
    :class "size-4"}
   [:path
    {:stroke-linecap "round",
     :stroke-linejoin "round",
     :d "m4.5 15.75 7.5-7.5 7.5 7.5"}]])

(def info-icon
  [:svg
   {:xmlns "http://www.w3.org/2000/svg",
    :fill "none",
    :viewBox "0 0 24 24",
    :stroke-width "1.5",
    :stroke "currentColor",
    :class "size-5"}
   [:path
    {:stroke-linecap "round",
     :stroke-linejoin "round",
     :d
     "m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z"}]])

(defn vat-footnote
  "Renders the VAT exclusion footnote for price displays."
  []
  [:p.text-xs.text-gray-500.text-center.mt-2
   "* excl. VAT"])

(defn buy-button
  [w]
  (let [data (query/get-result w
                               subscription-query)
        uri (-> data
                (:checkout-url)
                (uri/uri))
        claim-campaign-command {:command/kind :command/claim-campaign
                                :command/data {}}
        redirect [[:js/location (-> uri
                                    (uri/assoc-query :interval
                                                     (case (get-billing-interval w)
                                                       :month
                                                       "monthly"
                                                       :year
                                                       "yearly")
                                                     :quantity
                                                     (long (js/Math.ceil (:receipt-total w))))
                                    (uri/uri-str))]]
        claim-and-redirect [[:data/command
                             claim-campaign-command
                             {:on-success redirect}]]
        issued? (command/issued? w claim-campaign-command)]
    [:button.w-full
     {:class (cons
               "text-base"
               ui/button-primary)
      :disabled issued?
      :on {:click claim-and-redirect}}
     (if issued?
       "Opening checkout..."
       (str "Sign up for "
            (format-price (js/Math.ceil (:receipt-total w)))
            "* per "
            (name (get-billing-interval w))))]))

(defn render-service-rows
  "Renders the table rows for a single service."
  [w service-id]
  (let [service (service-id (:services w))
        w* (assoc w :service service)
        {:keys [units-min units-max]} service
        disabled (= units-min units-max)]
    (list
      [:tr
       (cond (:marked service)
             {:class ["bg-yellow-100" "m-2"]}
             disabled
             {:class ["opacity-60"]})
       (service-cell w*)
       [:td
        [:div.flex.gap-1.items-center
         (:name service)
         [:div {:class ["relative" "inline-block" "group"]}
          info-icon
          [:span {:class ["absolute"
                          "bottom-full"
                          "left-1/2"
                          "transform"
                          "-translate-x-1/2"
                          "mb-2"
                          "w-48"
                          "bg-black"
                          "text-white"
                          "text-center"
                          "text-sm"
                          "py-1"
                          "px-2"
                          "rounded"
                          "opacity-0"
                          "group-hover:opacity-100"
                          "transition-opacity"
                          "duration-200"
                          "pointer-events-none"
                          "z-10"]}
           (:tooltip service)]]]]
       [:td (discount-badge (part/get-service-discount w* service-id))]
       (service-total w*)]
      [:tr
       (when disabled
         {:class ["opacity-60"]})
       [:td]
       [:td.text-gray-600
        {:colspan 2}
        (service-price-cell w*)]]
      [:tr.h-2])))

(defn optional-services-toggle
  "Renders the toggle row for optional services section."
  [w]
  (let [expanded? (:show-optional-services w)]
    [:tr
     [:td {:colspan 4}
      [:div.flex.items-center.cursor-pointer.py-2.text-gray-600.hover:text-gray-800
       {:on {:click [[:store/assoc :show-optional-services (not expanded?)]]}}
       (if expanded? chevron-up-icon chevron-down-icon)
       [:span.ml-2.text-xs.font-medium "Optional services"]]]]))

(defn table
  [w]
  (let [show-optional? (:show-optional-services w)]
    [:div.flex.flex-col.gap-3.pb-6.pt-2.text-xs
     [:table.w-full.text-left
      [:thead
       [:tr
        [:th "Product"]
        [:th.w-full]
        [:th]
        [:th.py-2 {:class ["text-right"]}
         "Total"]]]
      [:tbody
       ;; Core services (always visible)
       (mapcat #(render-service-rows w %) core-services)

       ;; Optional services toggle
       (optional-services-toggle w)

       ;; Optional services (collapsible) are hidden to reduce the complexity
       ;; for the user when making their purchase decision:
       (when show-optional?
         (mapcat #(render-service-rows w %) optional-services))

     [:tr.h-4
      [:td]
      [:td]
      [:td]
      [:td]]
     [:tr.border-t-2
      [:td]
      [:td]
      [:td]
      [:td]]
     (when (and (:total-savings w) (pos? (:total-savings w)))
       [:tr
        [:td {:colspan 3
              :class ["text-sm" "text-green-600" "font-semibold"]}
         "You save:"]
        [:td {:class ["text-right" "text-green-600" "font-semibold"]}
         (str "-" (format-price (:total-savings w)))]])
     [:tr
      [:td.font-semibold
       {:colspan 3}
       [:div.flex
        [:span
         {:class ["text-sm/6"]}
         "Buy per "]
        (let [class ["ml-3" "relative" "size-4" "appearance-none" "rounded-full" "border" "border-gray-300" "bg-white" "before:absolute" "before:inset-1" "before:rounded-full" "before:bg-white" "checked:border-indigo-600" "checked:bg-indigo-600" "focus-visible:outline" "focus-visible:outline-2" "focus-visible:outline-offset-2" "focus-visible:outline-indigo-600" "disabled:border-gray-300" "disabled:bg-gray-100" "disabled:before:bg-gray-400" "forced-colors:appearance-auto" "forced-colors:before:hidden" "[&:not(:checked)]:before:hidden"]
              billing-interval (get-billing-interval w)]
          [:div
           [:div
            {:on {:click [[:store/assoc :billing-interval :month]]}}
            [:input
             {:type "radio",
              :checked (= billing-interval
                          :month)
              :class class}]
            [:label
             {:class ["ml-2" "text-sm/6" "font-medium" "text-gray-900"]}
             "Month"]]
           [:div
            {:on {:click [[:store/assoc :billing-interval :year]]}}
            [:input
             {:type "radio",
              :checked (= billing-interval
                          :year)
              :class class}]
            [:label
             {:class ["ml-2" "text-sm/6" "font-medium" "text-gray-900"]}
             "Year"]]])]]
      [:td {:class ["flex" "text-sm/6" "font-semibold" "justify-end"]}
       (format-price (:receipt-total w))]]]]
   ]))

(defn support-panel
  []
  [:div.py-4.justify-center.items-center.flex.flex-col.gap-2.text-sm
   [:a.flex.gap-2.items-center.text-gray-600
    {:class ["hover:text-gray-700" "hover:underline"]
     :href "mailto:support@storrito.com"}
    [:svg {:xmlns "http://www.w3.org/2000/svg"
           :width "16"
           :height "16"
           :viewBox "0 0 24 24"
           :fill "none"}
     [:path
      {:d
       "M21.3 12.23h-3.48c-.98 0-1.85.54-2.29 1.42l-.84 1.66c-.2.4-.6.65-1.04.65h-3.28c-.31 0-.75-.07-1.04-.65l-.84-1.65a2.567 2.567 0 0 0-2.29-1.42H2.7c-.39 0-.7.31-.7.7v3.26C2 19.83 4.18 22 7.82 22h8.38c3.43 0 5.54-1.88 5.8-5.22v-3.85c0-.38-.31-.7-.7-.7ZM12.75 2c0-.41-.34-.75-.75-.75s-.75.34-.75.75v2h1.5V2Z"
       :fill "#777"}]
     [:path
      {:d
       "M22 9.81v1.04a2.06 2.06 0 0 0-.7-.12h-3.48c-1.55 0-2.94.86-3.63 2.24l-.75 1.48h-2.86l-.75-1.47a4.026 4.026 0 0 0-3.63-2.25H2.7c-.24 0-.48.04-.7.12V9.81C2 6.17 4.17 4 7.81 4h3.44v3.19l-.72-.72a.754.754 0 0 0-1.06 0c-.29.29-.29.77 0 1.06l2 2c.01.01.02.01.02.02a.753.753 0 0 0 .51.2c.1 0 .19-.02.28-.06.09-.03.18-.09.25-.16l2-2c.29-.29.29-.77 0-1.06a.754.754 0 0 0-1.06 0l-.72.72V4h3.44C19.83 4 22 6.17 22 9.81Z"
       :fill "#777"}]]
    "support@storrito.com"]
   ])

(defn advanced-calculator
  [w]
  (let [w (-> w
              (part/add-instagram-accounts)
              (part/add-services)
              (part/add-service-order)
              (set-current-instagram-seats)
              (part/add-receipt-total)
              (part/add-total-savings))]
    [:div.flex.items-center.justify-center
     [:div.rounded.bg-gray-50.px-6.pt-8.shadow-xl.bg-white
      {:style {:width "28rem"}}
      [:img.mx-auto.h-24.py-4
       {:src
        "/img/logo/burrito.svg"}]
      [:div.flex.flex-col.justify-center.items-center.gap-2
       [:h4.font-semibold "storrito.com"]
       [:p.text-xs "Your receipt preview calculator"]]
      (intro)
      [:div.flex.flex-col.gap-3.border-b.py-6.text-xs
       [:p.flex.justify-between
        [:span.text-gray-400 "Order Type:"]
        [:span "Subscription"]]
       [:p.flex.justify-between
        [:span.text-gray-400 "Customer:"]
        [:span (:user/email
                 (query/get-result w
                                   {:query/kind :query/user-session}))]]]
      (list
        (table w)
        (savings-panel w)
        ;; Refill UI hidden for now:
        (buy-button w)
        (vat-footnote)
        (support-panel))
      [:p.pb-6.text-center
       [:a.text-sm.text-blue-600.font-medium.hover:underline
        {:href (calendly/url :solutions "plan-calculator" (user-session/org-uuid w))
         :target "_blank"}
        "Need help deciding? Book a call with our team."]]]]))

(defn same-plan?
  [w]
  (= [(:selected-interval w) (:selected-quantity w)]
     [(:current-interval w) (:current-quantity w)]))

(defn add-charges
  [w]
  (assoc w
         :charges
         (if (= (:current-interval w)
                (:selected-interval w))
           {:due-today (- (:selected-quantity w)
                          (:current-quantity w))
            :next-billed-at (:next_billed_at (:subscription-item w))}
           {:due-today (:selected-quantity w)
            :next-billed-at (t/>> (t/date)
                                  (t/new-period 1
                                                (if (= (:selected-interval w) "month")
                                                  :months
                                                  :years)))})))

(defn upgrade-button
  [w]
  [:button.w-full
   {:class (cons
             "text-base"
             ui/button-primary)
    :disabled (command/issued? w
                               (:subscription-update-command w))
    :on {:click [[:js/confirm (str "Your plan will be adjusted to " (:selected-quantity w)
                                   " USD per " (:selected-interval w) "."
                                   )]
                 [:data/command
                  (:subscription-update-command w)
                  {:on-success (concat core/query-actions
                                       [:store/assoc-in
                                        [:location]
                                        {:location/page-id :pages/subscription}])}]]}}
   (if (command/issued? w
                        (:subscription-update-command w))
     "Updating subscription..."
     (str "Upgrade to $" (Math/ceil (:receipt-total w)) "*"))])

(defn change-plan-button
  [w]
  (let [charges (:charges w)]
    [:button.w-full
     {:class (cons
               "text-base"
               ui/button-primary)
      :disabled (or (command/issued? w
                                     (:subscription-update-command w))
                    (same-plan? w))
      :on {:click [[:js/confirm (str "Your plan will be " (:selected-quantity w)
                                     " USD per " (:selected-interval w) " excl. taxes."
                                     " You will be charged " (if (pos? (:due-today charges))
                                                               (str "$" (:due-today charges) " USD excl. taxes")
                                                               "0 USD")
                                     " today and your next payment is due " (subs (str (:next-billed-at charges))
                                                                                  0 10))]
                   [:data/command
                    (:subscription-update-command w)
                    {:on-success (concat core/query-actions
                                         [core/repeat-import-and-refresh
                                          [:store/assoc-in
                                           [:location]
                                           {:location/page-id :pages/subscription}]])}]]}}
     (cond
       (command/issued? w
                        (:subscription-update-command w))
       "Updating subscription..."

       (same-plan? w)
       "This is your current plan"

       :else
       [:div.text-white
        (str
          "Change to $" (:selected-quantity w)
          "* per " (:selected-interval w))]
       )]))

(defn get-price-id
  [w]
  (case (get-billing-interval w)
    :month (:monthly-price-id (:prices w))
    :year (:yearly-price-id (:prices w))))

(defn add-subscription-update-command
  [w]
  (assoc w
         :subscription-update-command
         {:command/kind :command/subscription-update
          :command/data {:price-id (get-price-id w)
                         :quantity (:selected-quantity w)}}))

(defn upgrade-table
  [w]
  (let [w (-> w
              (part/add-prices)
              (part/add-instagram-accounts)
              (part/add-services)
              (part/add-service-order)
              (part/update-instagram-seats)
              (part/add-receipt-total)
              (add-selected-quantity)
              (add-selected-interval)
              (add-current-quantity)
              (add-current-interval)
              (part/add-total-savings)
              (add-subscription-update-command))]
    [:div {:class ui.elements/panel}
     (table w)
     (upgrade-button w)
     (vat-footnote)
     (error-panel w)
     (support-panel)]))

(defn calculator
  [w]
  (advanced-calculator w))

(defn due-panel
  [w]
  (let [charges (:charges w)]
    [:div.flex.flex-col.w-full.items-center.justify-center.mt-3
     {:class ["text-xs" "text-gray-800"]}
     [:span
      "Due today: " (if (pos? (:due-today charges))
                      (str "$" (:due-today charges) " USD")
                      "0 USD")]
     [:span
      "Next due: " (subs (str (:next-billed-at charges))
                         0 10)]]))

(defn warning-panel
  [w]
  (let [{:keys [upgrade? minimum-top-up]} (part/suggest-upgrade
                                            (assoc w
                                                   :billing-interval (keyword (:selected-interval w))
                                                   :quantity (:selected-quantity w)
                                                   :instagram-account-count (count (:instagram-accounts w))))]
    (when upgrade?
      [:div {:class ["shadow-sm"
                     "rounded"
                     "bg-yellow-200"
                     "p-2"
                     "text-yellow-800"
                     "text-xs"
                     "mt-4"]}
       [:span.flex.ml-4.items-center
        [:i.fa-solid.fa-triangle-exclamation]
        [:span.ml-4
         "We suggest a minimum of " [:b "$" minimum-top-up " / " (:selected-interval w)] " to cover your connected Instagram accounts."]]])))

(defn change-plan-table
  [w]
  [:div.flex.items-center.justify-center.flex-grow
   [:div
    {:class (conj ui.elements/panel
                  "text-center"
                  "flex-grow")}
    (list
      (table w)
      (savings-panel w)
      (change-plan-button w)
      (vat-footnote)
      (warning-panel w)
      (error-panel w))]])

(defn change-plan-panel
  [w]
  (let [w (-> w
              (part/add-subscription-item)
              (part/add-prices)
              (part/add-instagram-accounts)
              (part/add-services)
              (part/add-service-order)
              (set-current-instagram-seats)
              (part/add-receipt-total)
              (part/add-total-savings)
              (add-selected-quantity)
              (add-selected-interval)
              (add-current-quantity)
              (add-current-interval)
              (add-charges)
              (add-subscription-update-command))]
    [:div.flex.flex-col.items-center.w-full
     [:div {:class ["flex" "flex-col" "md:flex-row" "gap-6" "items-start" "w-full" "flex-grow"]}
      ;; Left Panel (Current Plan)
      [:div
       {:class ["flex" "md:flex-col" "gap-6"]}
       [:div
        {:class (conj ui.elements/panel
                      "text-center"
                      "min-w-40")}
        [:h2 {:class ["text-lg" "font-medium" "text-gray-800"]}
         "Current Plan"]
        [:p {:class ["text-2xl" "font-semibold" "text-gray-800" "mb-1"]}
         (str "$" (:current-quantity w))
         [:span {:class ["text-base" "font-normal" "text-gray-500"]}
          " / " (:current-interval w)]]]

       [:div
        {:class (conj ui.elements/panel
                      "text-center")}
        [:h2 {:class ["text-lg" "font-medium" "text-gray-800"]}
         "New Plan"]
        [:p {:class ["text-2xl" "font-semibold" "text-gray-800" "mb-1"]}
         (str "$" (:selected-quantity w))
         [:span {:class ["text-base" "font-normal" "text-gray-500"]}
          " / " (:selected-interval w)]]
        (due-panel w)]]
      [:div.flex.flex-col.w-full
       (change-plan-table w)
       [:p {:class ["text-sm" "text-gray-700" "mt-2" "w-full" "text-center"]}
        "All prices exclusive applicable taxes"]]]
     ]
    ))
